import React, { useMemo } from 'react';
import { Group, Circle, Rect, Text, Line } from 'react-konva';
import { CELL_SIZE } from './GridLayer';
import { useCampaign } from '../../contexts/CampaignContext';
import { useAuth } from '../../contexts/AuthContext';
import { teleportStyleMeta } from '../../lib/teleports';

// Renders teleport endpoints on the map. Visibility rules:
//   - Non-hidden teleports: visible to everyone (DM + players).
//   - Hidden trapdoors: visible to DM always; visible to players only when
//     `discoveredBy[viewerId]` is set. Mechanics fire regardless of discovery
//     state — the surprise is the whole point.
//
// `bare` matches the static-stack fragment convention so we share one Konva
// canvas with the other static layers (Tier 1 perf).

function Endpoint({ x, y, style, dashed, faded, label }) {
  const meta = teleportStyleMeta(style);
  const cx   = (x + 0.5) * CELL_SIZE;
  const cy   = (y + 0.5) * CELL_SIZE;
  const size = CELL_SIZE * 0.7;
  const half = size / 2;
  const stroke = meta.color;
  const dashArr = dashed ? [4, 4] : null;
  const opacity = faded ? 0.45 : 1;

  return (
    <Group x={cx} y={cy} opacity={opacity} listening={false}>
      <Rect
        x={-half}
        y={-half}
        width={size}
        height={size}
        cornerRadius={size * 0.18}
        stroke={stroke}
        strokeWidth={2}
        dash={dashArr}
        fill="rgba(8,12,28,0.6)"
        perfectDrawEnabled={false}
      />
      <Text
        text={meta.icon}
        fontSize={CELL_SIZE * 0.45}
        fontStyle="bold"
        fill={stroke}
        align="center"
        verticalAlign="middle"
        offsetX={CELL_SIZE * 0.18}
        offsetY={CELL_SIZE * 0.22}
        listening={false}
        perfectDrawEnabled={false}
      />
      {label && (
        <Text
          text={label}
          fontSize={9}
          fill="#9a8b6e"
          align="center"
          offsetX={CELL_SIZE * 0.4}
          offsetY={-CELL_SIZE * 0.42}
          width={CELL_SIZE * 0.8}
          listening={false}
          perfectDrawEnabled={false}
        />
      )}
    </Group>
  );
}

export default function TeleportLayer({ isDm = false, bare = false, draftA = null, draftMouse = null, draftStyle = 'portal' }) {
  const { teleports } = useCampaign();
  const { auth } = useAuth();
  const viewerPlayerId = auth?.playerId || null;

  const list = useMemo(() => Object.values(teleports || {}), [teleports]);

  const body = (
    <>
      {list.map((tp) => {
        // Visibility check
        const discovered = viewerPlayerId && tp.discoveredBy?.[viewerPlayerId];
        if (tp.hidden && !isDm && !discovered) return null;

        const showDestFaded = !tp.twoWay; // one-way destination is just a hint to DM
        const dashedA = tp.hidden && isDm && !discovered;
        const dashedB = tp.hidden && isDm && !discovered;

        const aShown = !tp.hidden || isDm || discovered;
        const bShown = !tp.hidden || isDm || discovered;
        if (!aShown && !bShown) return null;

        // Destination pip for one-way: rendered fainter on the B endpoint
        // unless this is two-way (then full pip on both).
        const destPipOnB = !tp.twoWay;

        // Faded B for one-way to non-DM viewers — players never see B for
        // a one-way teleport (it's a destination only).
        const hideBToPlayer = !tp.twoWay && !isDm;

        return (
          <Group key={tp.id} listening={false}>
            {aShown && (
              <Endpoint x={tp.a.x} y={tp.a.y} style={tp.style} dashed={dashedA} faded={false} label={tp.label} />
            )}
            {bShown && !hideBToPlayer && (
              <Endpoint x={tp.b.x} y={tp.b.y} style={tp.style} dashed={dashedB} faded={destPipOnB && isDm} label={tp.label} />
            )}
            {/* DM-only thin connector line between endpoints. */}
            {isDm && (
              <Line
                points={[
                  (tp.a.x + 0.5) * CELL_SIZE, (tp.a.y + 0.5) * CELL_SIZE,
                  (tp.b.x + 0.5) * CELL_SIZE, (tp.b.y + 0.5) * CELL_SIZE,
                ]}
                stroke={teleportStyleMeta(tp.style).color}
                strokeWidth={1}
                opacity={0.18}
                dash={[3, 5]}
                listening={false}
                perfectDrawEnabled={false}
              />
            )}
          </Group>
        );
      })}

      {/* Authoring preview — first endpoint placed, second following the cursor */}
      {isDm && draftA && (
        <Endpoint x={draftA.x} y={draftA.y} style={draftStyle} dashed={true} faded={false} />
      )}
      {isDm && draftA && draftMouse && (
        <Line
          points={[
            (draftA.x + 0.5) * CELL_SIZE, (draftA.y + 0.5) * CELL_SIZE,
            (draftMouse.x + 0.5) * CELL_SIZE, (draftMouse.y + 0.5) * CELL_SIZE,
          ]}
          stroke={teleportStyleMeta(draftStyle).color}
          strokeWidth={1.5}
          opacity={0.6}
          dash={[4, 4]}
          listening={false}
          perfectDrawEnabled={false}
        />
      )}
    </>
  );

  return bare ? body : <>{body}</>;
}
